import { AppContext } from '../context/AppContext'
import {
  ConsumerSupportCaseCategory,
  ConsumerSupportCaseStatus,
  EventLog,
  Order,
  Package,
  PackageEventOperations,
  ProofOfServicePhotoUrl,
} from '@consumer/graphql'
import { sortedList } from '../utils/displayUtils'
import { STATSIG_FEATURE_GATES } from '@consumer/shared'
import { headerFromPackageStatus } from '../utils/headerTextUtil'
import { colors } from '../utils/theme/configureTheme'
import DeliveryPhotoCarousel, { DeliveryPhoto } from './DeliveryPhotoCarousel'
import DeliveryPhotoWithRating from './DeliveryPhotoWithRating'
import { ProofOfServicePhoto } from './Map/OrderMap'
import { PackageEvent } from './PackageEvents/PackageEvent'

import AsyncStorage from '@react-native-async-storage/async-storage'
import { useGateValue } from '@statsig/react-bindings'
import { DateTime } from 'luxon'
import { Button, Center, Heading, Image, Modal, Pressable, Text, VStack, Box } from 'native-base'
import { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { isMobile } from 'react-device-detect'
import { useNavigate } from 'react-router-dom'
import { PackageRequiredPinStatus } from './signatureByPin/signatureByPin'

interface ExistingSupportCase {
  caseCategory: ConsumerSupportCaseCategory
  caseStatus: ConsumerSupportCaseStatus
  caseId: string
  trackingId: string
}

/**
 * Component to display package information and event log
 */
export default function PackageInfoWithCollapsibleEventLog({
  pkg,
  showBottomBorder = false,
  proofOfServicePhotos,
}: {
  pkg: Package
  showBottomBorder?: boolean
  proofOfServicePhotos?: ProofOfServicePhoto[]
}) {
  const [existingSupportCases, setExistingSupportCases] = useState<ExistingSupportCase[]>([])
  const { order } = useContext(AppContext)
  const supportEscalationButtonGate = useGateValue(STATSIG_FEATURE_GATES.cwa_support_escalation)

  useEffect(() => {
    let mounted = true
    ;(async () => {
      const packages = order?.packages ?? []
      const results = await Promise.all(
        packages.map(async pkg => {
          const stored = await AsyncStorage.getItem(`supportCase_${pkg.trackingId}`)
          if (stored) {
            return JSON.parse(stored) as ExistingSupportCase
          }
          return null
        })
      )
      const existingCases = results.filter((c): c is ExistingSupportCase => c != null)
      if (mounted) setExistingSupportCases(existingCases)
    })()
    return () => {
      mounted = false
    }
  }, [order?.packages])

  return (
    <Box borderBottomColor={showBottomBorder ? '1px solid #C3C3BE' : ''}>
      <PackageStatusHeader
        pkg={pkg}
        order={order!}
        clientDisplayName={order?.clientDisplayName ?? ''}
        deliveryDate={order?.deliveryDate ?? ''}
        _proofOfServicePhotoUrls={proofOfServicePhotos}
        supportEscalationButtonGate={supportEscalationButtonGate}
        pkgHasExistingSupportCase={
          !!existingSupportCases.find(supportCase => supportCase.trackingId === pkg.trackingId)
        }
      />
      <PackageRequiredPinStatus pkg={pkg} />
      <CollapsiblePackageEventLog
        _packageDetails={pkg}
        _clientDisplayName={order?.clientDisplayName ?? ''}
        _serviceType={order?.serviceType as string}
        key={pkg.trackingId}
        order={order!}
        _proofOfServicePhotoUrls={proofOfServicePhotos}
        supportEscalationButtonGate={supportEscalationButtonGate}
        pkgHasExistingSupportCase={
          !!existingSupportCases.find(supportCase => supportCase.trackingId === pkg.trackingId)
        }
      />
    </Box>
  )
}

const PackageStatusHeader = ({
  pkg,
  clientDisplayName,
  deliveryDate,
  _proofOfServicePhotoUrls,
  supportEscalationButtonGate = false,
  pkgHasExistingSupportCase = false,
  order,
}: {
  pkg: Package
  order: Order
  clientDisplayName: string
  deliveryDate: string
  _proofOfServicePhotoUrls?: ProofOfServicePhotoUrl[]
  supportEscalationButtonGate: boolean
  pkgHasExistingSupportCase: boolean
}) => {
  const lastEventOperation = pkg?.eventLog[0]?.operation
  const isDelivered = lastEventOperation === PackageEventOperations.Delivered
  const isMisdelivered = lastEventOperation === PackageEventOperations.Misdelivered
  // Show the POD on the tracking page header for both terminal delivery outcomes so consumers
  // can still see what the driver captured even if the package was later flagged as misdelivered.
  const shouldShowPodPhoto = isDelivered || isMisdelivered
  const packageUrl = _proofOfServicePhotoUrls?.find(packageUrl => packageUrl.packageId === pkg.packageId)
  const [photoModalVisible, setPhotoModalVisible] = useState<boolean>(false)
  const [selectedPhotoUrl, setSelectedPhotoUrl] = useState<string>(packageUrl?.url ?? '')
  const [existingCsatRating, setExistingCsatRating] = useState<number | undefined>(undefined)
  const multiPOD = useGateValue(STATSIG_FEATURE_GATES.three_delivery_photos_on_tracking_page)

  // Build ordered photo list: POD → Unit → Address (filter out nulls)
  const deliveryPhotos: DeliveryPhoto[] = [
    packageUrl?.url ? { url: packageUrl.url } : null,
    packageUrl?.unitPhotoUrl && multiPOD ? { url: packageUrl.unitPhotoUrl } : null,
    packageUrl?.addressPhotoUrl && multiPOD ? { url: packageUrl.addressPhotoUrl } : null,
  ].filter((p): p is DeliveryPhoto => p !== null)

  useEffect(() => {
    if (photoModalVisible) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = 'visible'
    }
  }, [photoModalVisible])

  useEffect(() => {
    const loadExistingRating = async () => {
      try {
        const storedData = await AsyncStorage.getItem(`csatRating_${pkg.trackingId}`)
        if (storedData) {
          const ratingData = JSON.parse(storedData)
          if (ratingData.trackingId === pkg.trackingId && ratingData.csatRating) {
            setExistingCsatRating(ratingData.csatRating)
          }
        }
      } catch (error) {
        console.error('Failed to load existing CSAT rating:', error)
      }
    }

    loadExistingRating()
  }, [pkg.trackingId])

  const header = headerFromPackageStatus({ pkg, order, clientDisplayName, deliveryDate })

  const saveCsatRating = useCallback(
    async (csatRating: number) => {
      const ratingData = {
        csatRating,
        trackingId: pkg.trackingId,
      }

      // TO DO: refactor into util once web based feedback is added
      try {
        await AsyncStorage.setItem(`csatRating_${pkg.trackingId}`, JSON.stringify(ratingData))
        setExistingCsatRating(csatRating)
      } catch (error) {
        console.error('Failed to save CSAT rating:', error)
      }
    },
    [pkg.trackingId]
  )

  return (
    <VStack mb={3} px={2}>
      <Center>
        {/* TO DO: Migrate this modal to Overlay Provider to be able to open photo modal from map component */}
        <Modal
          backgroundColor="rgba(0, 0, 0, 0.0)"
          isOpen={photoModalVisible}
          onClose={() => setPhotoModalVisible(false)}
          avoidKeyboard
        >
          <Modal.Content backgroundColor="rgba(0, 0, 0, 0.0)" style={{ shadowOpacity: 0 }} width={'100%'}>
            <Modal.Body>
              <Image
                src={selectedPhotoUrl}
                borderRadius="5px"
                width={undefined}
                height={'100%'}
                style={{ aspectRatio: 3 / 4 }}
                alt={'Proof of Service Photo'}
              />
              <Pressable
                onPress={() => setPhotoModalVisible(!photoModalVisible)}
                style={{ position: 'absolute', top: 35, right: 33 }}
              >
                <svg xmlns="http://www.w3.org/2000/svg" width={32} height={32} fill="none">
                  <rect width={32} height={32} fill="#21211F" rx={16} />
                  <path
                    fill="#fff"
                    d="M21.03 19.97a.751.751 0 0 1-1.062 1.062L16 17.062l-3.97 3.969a.751.751 0 1 1-1.062-1.063L14.938 16l-3.969-3.97a.752.752 0 0 1 1.063-1.062L16 14.938l3.97-3.97a.751.751 0 1 1 1.062 1.062L17.062 16l3.969 3.97Z"
                  />
                </svg>
              </Pressable>
            </Modal.Body>
          </Modal.Content>
        </Modal>
      </Center>
      <Heading style={{ fontSize: isMobile ? 24 : 32 }} fontFamily={'bodyBold'} fontWeight="700" lineHeight={32}>
        {header?.header}
      </Heading>
      <Text style={{ fontSize: isMobile ? 14 : 18 }} fontWeight="400" lineHeight={22}>
        {header?.subheader}
      </Text>
      {shouldShowPodPhoto && deliveryPhotos.length > 0 && (
        <>
          <DeliveryPhotoCarousel
            photos={deliveryPhotos}
            onPhotoPress={url => {
              setSelectedPhotoUrl(url)
              setPhotoModalVisible(true)
            }}
          />
          {supportEscalationButtonGate && !pkgHasExistingSupportCase && (
            <DeliveryPhotoWithRating
              packageUrl={packageUrl!}
              trackingId={pkg.trackingId}
              photoModalVisible={photoModalVisible}
              setPhotoModalVisible={setPhotoModalVisible}
              existingCsatRating={existingCsatRating}
              onSaveCsatRating={saveCsatRating}
              hidePhoto={!shouldShowPodPhoto}
            />
          )}
        </>
      )}
    </VStack>
  )
}

const CollapsiblePackageEventLog = ({
  _packageDetails,
  _clientDisplayName,
  _serviceType,
  order,
  _proofOfServicePhotoUrls,
  supportEscalationButtonGate = false,
  pkgHasExistingSupportCase = false,
}: {
  _packageDetails: Package
  _clientDisplayName: string
  _serviceType: string
  order: Order
  _proofOfServicePhotoUrls?: ProofOfServicePhotoUrl[]
  supportEscalationButtonGate: boolean
  pkgHasExistingSupportCase: boolean
}) => {
  const navigate = useNavigate()
  const [sortedDetails, setSortedDetails] = useState<EventLog[]>([])
  const [eventLogExpanded, setEventLogExpanded] = useState<boolean>(false)
  const packageUrl = _proofOfServicePhotoUrls?.find(packageUrl => packageUrl.packageId === _packageDetails.packageId)
  const isDelivered = _packageDetails?.eventLog[0]?.operation === PackageEventOperations.Delivered
  useEffect(() => {
    setSortedDetails(sortedList(_packageDetails))
  }, [_packageDetails])

  const handleToggleExpandEventLog = () => {
    setEventLogExpanded(!eventLogExpanded)
  }

  const timeZone = _packageDetails.timeZone ?? order?.timeZone ?? 'America/Denver'

  const showSupportEscalationButton = useMemo(() => {
    const timeZone = _packageDetails.timeZone ?? order.timeZone!
    const currentLocalDateTime = DateTime.local({ zone: _packageDetails.timeZone ?? order.timeZone! })
    // TODO: revisit whether ?? is appropriate here (the || is intentional for boolean coercion)
    // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
    const pkgHasValidDeliveryDate: boolean = order?.deliveryDateInfo?.date || _packageDetails.scheduledServiceDate
    const pkgSheduledServiceDateLocalDateTime = DateTime.fromISO(
      order?.deliveryDateInfo?.date ?? _packageDetails.scheduledServiceDate,
      { zone: timeZone }
    )
    const daysSinceDeliveryDate = currentLocalDateTime.diff(pkgSheduledServiceDateLocalDateTime, 'days').days

    return (
      supportEscalationButtonGate &&
      pkgHasValidDeliveryDate &&
      !pkgHasExistingSupportCase &&
      ((!packageUrl?.url && isDelivered) || (!packageUrl?.url && !isDelivered && daysSinceDeliveryDate >= 1))
    )
  }, [
    order?.deliveryDateInfo,
    _packageDetails.scheduledServiceDate,
    _packageDetails.timeZone,
    order?.timeZone,
    supportEscalationButtonGate,
    packageUrl,
    isDelivered,
    pkgHasExistingSupportCase,
  ])

  return (
    <>
      <Box key={_packageDetails.packageId} pl={1}>
        {!eventLogExpanded ? (
          <PackageEvent
            trackingId={_packageDetails.trackingId}
            details={sortedDetails[0]}
            serviceType={_serviceType}
            clientDisplayName={_clientDisplayName}
            showBorder={false}
            isMostCurrentEvent={true}
            onClick={() => {
              if (sortedDetails.length > 1) {
                handleToggleExpandEventLog()
              }
            }}
            packageEventLogExpanded={eventLogExpanded}
            timeZone={timeZone}
            packageHasMultipleEvents={sortedDetails.length > 1}
          />
        ) : (
          sortedDetails.map((details, index) => (
            <PackageEvent
              trackingId={_packageDetails.trackingId}
              details={details}
              serviceType={_serviceType}
              clientDisplayName={_clientDisplayName}
              showBorder={sortedDetails.length - 1 !== index}
              isMostCurrentEvent={index === 0}
              onClick={() => (index === 0 ? handleToggleExpandEventLog() : null)}
              packageEventLogExpanded={eventLogExpanded}
              timeZone={timeZone}
              packageHasMultipleEvents={sortedDetails.length > 1}
            />
          ))
        )}
      </Box>
      <Box display="flex" flexDirection="column" alignItems="center">
        {showSupportEscalationButton && (
          <Button
            backgroundColor="transparent"
            borderWidth={2}
            borderColor={colors.vermillion[300]}
            _text={{
              color: colors.vermillion[300],
              fontWeight: '500',
              fontSize: '14px',
              lineHeight: '125%',
              letterSpacing: '-1%',
              fontFamily: 'Matter Regular',
            }}
            py={2}
            px={4}
            mb={3}
            maxWidth="300px"
            width="75%"
            onPress={() => navigate(`/reportIssue/${_packageDetails.trackingId}`)}
          >
            Report an Issue
          </Button>
        )}
      </Box>
    </>
  )
}
